// Claude is called via Supabase Edge Function — no API key needed in the app
// analyzeRoomPhoto: estimates room dimensions from a photo using Claude vision
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from './supabase';

export async function generateQuote(jobDescription, tradeType, settings = {}) {
  if (!jobDescription) throw new Error('Please describe the job first.');

  // Use direct fetch with anon key as Bearer — this is the standard Supabase pattern for edge functions
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);

  let res;
  try {
    res = await fetch(`${SUPABASE_URL}/functions/v1/generate-quote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        jobDescription,
        tradeType,
        markupPercent: settings.markupPercent || 0,
        taxRate: settings.taxRate || 0,
        location: settings.location || '',
      }),
      signal: controller.signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('Generation timed out. Check that your ANTHROPIC_API_KEY is set in Supabase → Settings → Edge Functions → Secrets.');
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    console.error('Edge function error:', res.status, errText);
    let errMsg = `Generation failed (${res.status}).`;
    try { errMsg = JSON.parse(errText).error || errMsg; } catch {}
    throw new Error(errMsg);
  }

  const parsed = await res.json();

  if (parsed.error) {
    console.error('Edge function error:', parsed.error);
    throw new Error(parsed.error || 'Generation failed. Please try again.');
  }

  if (!parsed.lineItems || !Array.isArray(parsed.lineItems)) {
    throw new Error('Unexpected response from AI. Please try again.');
  }

  const normalizedItems = parsed.lineItems.map((item) => ({
    description: item.description || '',
    quantity: Number(item.quantity) || 0,
    unit: item.unit || 'each',
    unitPrice: Number(item.unitPrice) || 0,
    total: Number(item.quantity || 0) * Number(item.unitPrice || 0),
  }));

  return {
    lineItems: normalizedItems,
    scopeOfWork: parsed.scopeOfWork || '',
    estimatedDays: Number(parsed.estimatedDays) || 1,
    notes: parsed.notes || '',
  };
}

export async function analyzePhoto(imageUrl, tradeType, settings = {}, imageBase64 = null, mediaType = null) {
  if (!imageUrl && !imageBase64) throw new Error('No image provided.');

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 45000); // longer timeout for image processing

  let res;
  try {
    res = await fetch(`${SUPABASE_URL}/functions/v1/analyze-photo`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        imageUrl: imageBase64 ? null : imageUrl,
        imageBase64,
        mediaType,
        tradeType,
        markupPercent: settings.markupPercent || 0,
        taxRate: settings.taxRate || 0,
        location: settings.location || '',
      }),
      signal: controller.signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('Photo analysis timed out. Please try again.');
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    let errMsg = `Analysis failed (${res.status}).`;
    try { errMsg = JSON.parse(errText).error || errMsg; } catch {}
    throw new Error(errMsg);
  }

  const parsed = await res.json();
  if (parsed.error) throw new Error(parsed.error);
  if (!parsed.lineItems || !Array.isArray(parsed.lineItems)) {
    throw new Error('Unexpected response from AI. Please try again.');
  }

  const normalizedItems = parsed.lineItems.map((item) => ({
    description: item.description || '',
    quantity: Number(item.quantity) || 0,
    unit: item.unit || 'each',
    unitPrice: Number(item.unitPrice) || 0,
    total: Number(item.quantity || 0) * Number(item.unitPrice || 0),
  }));

  return {
    lineItems: normalizedItems,
    scopeOfWork: parsed.scopeOfWork || '',
    estimatedDays: Number(parsed.estimatedDays) || 1,
    notes: parsed.notes || '',
    jobDescription: parsed.jobDescription || '',
  };
}

export async function analyzeRoomPhoto(imageUrl, imageBase64, mediaType) {
  if (!imageUrl && !imageBase64) throw new Error('No image provided.');

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 45000);

  let res;
  try {
    res = await fetch(`${SUPABASE_URL}/functions/v1/scan-room`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ imageUrl, imageBase64, mediaType }),
      signal: controller.signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('Room scan timed out. Please try again.');
    throw err;
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    let errMsg = `Scan failed (${res.status}).`;
    try { errMsg = JSON.parse(errText).error || errMsg; } catch {}
    throw new Error(errMsg);
  }

  const parsed = await res.json();
  if (parsed.error) throw new Error(parsed.error);

  return {
    length: parsed.length || 12,
    width: parsed.width || 10,
    height: parsed.height || 9,
    doors: parsed.doors || 1,
    windows: parsed.windows || 1,
    roomType: parsed.roomType || 'room',
    confidence: parsed.confidence || 'medium',
  };
}
